// Copyright 2020 TOKU Systems Inc.

import React, { useState, useEffect } from 'react';
import {
  SignalPlot,
  normalizeSignalPlot,
  makeSignalRanges,
  normalizeSummaries,
  mergeRanges,
} from './signal-plot';
import { List, Map } from 'immutable';
import { PlotDomain } from './plot-domain';
import {
  VictoryChart,
  VictoryBrushContainer,
  VictoryAxis,
  VictoryArea,
  VictoryLine,
  VictoryTheme,
} from 'victory';
import { VictoryBrushContainerProps } from 'victory-brush-container';
import { DomainTuple } from 'victory-core';
import moment from 'moment';
import { TimeSeries, SummaryPoint, TemporalResolution } from '@equestria/spike';
import illumass from '../../services/illumass';
import _ from 'lodash';
import ReactResizeDetector from 'react-resize-detector';
import { useSnackbar } from 'notistack';

interface TimelineProps {
  signalPlots: List<SignalPlot>;
  start?: moment.Moment;
  end?: moment.Moment;
  onBrushDomainChange?: (
    domain: DomainTuple,
    props: VictoryBrushContainerProps
  ) => void;
  timeDomain: DomainTuple;
  brushDomain: DomainTuple;
}

const weekMs = moment.duration('P1W').asMilliseconds();
const maxPoints = 120;

const makeTicks = (timeDomain: DomainTuple): List<number> => {
  const t: number[] = [];
  for (
    let currentTick = timeDomain[0].valueOf();
    currentTick < timeDomain[1].valueOf();
    currentTick += weekMs
  ) {
    t.push(currentTick);
  }
  return List(t);
};

export default function Timeline(props: TimelineProps): JSX.Element {
  const { signalPlots, onBrushDomainChange } = props;
  const timelineDomain: PlotDomain = {
    x: props.timeDomain,
    y: [0, 1],
  };

  const ticks: List<number> = makeTicks(timelineDomain.x);

  const [normalizedSignalPlots, setNormalizedSignalPlots] = useState(
    List<SignalPlot>()
  );
  const [normalizedDownscaledSummaries, setNormalizedDownscaledSummaries] =
    useState(Map<string, TimeSeries<SummaryPoint>>());
  const [summaries, setSummaries] = useState(
    Map<string, TimeSeries<SummaryPoint>>()
  );
  const [needToDownscale, setNeedToDownscale] = useState(false);

  const handleBrushDomainChange = (
    domain: PlotDomain,
    victoryBrushProps: VictoryBrushContainerProps
  ): void => {
    if (onBrushDomainChange) {
      onBrushDomainChange(domain.x, victoryBrushProps);
    }
  };

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setNormalizedSignalPlots(
      signalPlots.map((sp) =>
        normalizeSignalPlot(sp, {
          line: { strokeWidth: 0.6 },
          area: { strokeWidth: 0.4, fillOpacity: 0.2 },
        })
      )
    );
    setNeedToDownscale(true);
  }, [signalPlots]);

  useEffect(() => {
    if (!needToDownscale) {
      return;
    }
    setNeedToDownscale(false);

    const keysToRemove = summaries
      .keySeq()
      .toSet()
      .subtract(normalizedSignalPlots.map((sp) => sp.signal.id ?? ''));

    normalizedSignalPlots
      .reduce(
        (reduction, sp) =>
          reduction.then((loadedSummaries) => {
            const signalId = sp.signal?.id;
            if (signalId !== undefined && !loadedSummaries.has(signalId)) {
              if (sp.rawResolution === TemporalResolution.Hour) {
                return illumass.signalData
                  .raw(
                    `signal!${signalId}`,
                    moment.utc(timelineDomain.x[0].valueOf()),
                    moment.utc(timelineDomain.x[1].valueOf()),
                    TemporalResolution.Hour
                  )
                  .then((rawData) => {
                    return loadedSummaries.set(signalId, {
                      points: rawData.points.map((p) => ({
                        t: p.t,
                        mean: p.y,
                        min: p.y,
                        max: p.y,
                        count: 1,
                        variance: 0,
                      })),
                      measuringUnit: rawData.measuringUnit,
                    });
                  });
              } else {
                return illumass.signalData
                  .summary(
                    `signal!${signalId}`,
                    moment.utc(timelineDomain.x[0].valueOf()),
                    moment.utc(timelineDomain.x[1].valueOf()),
                    TemporalResolution.Hour
                  )
                  .then((summaryData) =>
                    loadedSummaries.set(signalId, summaryData)
                  );
              }
            } else {
              return loadedSummaries;
            }
          }),
        Promise.resolve(summaries)
      )
      .then((loadedSummaries) => {
        const domainDuration: moment.Duration = moment.duration(
          timelineDomain.x[1].valueOf() - timelineDomain.x[0].valueOf()
        );
        // Reduce this to ~120 points, or 6 hours per group (0.25 days)
        const groupDurationMillis = Math.floor(
          domainDuration.asMilliseconds() / maxPoints
        );
        const halfMillis = groupDurationMillis / 2;

        const downscaled: Map<
          string,
          TimeSeries<SummaryPoint>
        > = loadedSummaries.map((summaryData) => {
          const groupedSummaryData = _.groupBy(summaryData.points, (s) =>
            Math.floor((s.t + halfMillis) / groupDurationMillis).toString()
          );

          const downscaling: TimeSeries<SummaryPoint> = {
            points: [],
            measuringUnit: summaryData.measuringUnit,
          };

          for (const groupEntry of _.entries(groupedSummaryData)) {
            const groupKey = groupEntry[0];
            const group: SummaryPoint[] = groupEntry[1];
            downscaling.points.push(
              _.reduce(
                group,
                (prev: SummaryPoint, curr: SummaryPoint) => {
                  return {
                    t: prev.t,
                    min: Math.min(prev.min, curr.min),
                    max: Math.max(prev.max, curr.max),
                    variance: prev.variance + curr.variance,
                    count: prev.count + curr.count,
                    mean:
                      (prev.mean * prev.count + curr.mean * curr.count) /
                      (prev.count + curr.count), // risk of catostrophic cancellation
                  };
                },
                {
                  t: parseInt(groupKey) * groupDurationMillis + halfMillis,
                  mean: 0,
                  min: Number.MAX_VALUE,
                  max: Number.MIN_VALUE,
                  variance: 0,
                  count: 0,
                }
              )
            );
          }

          return downscaling;
        });

        const mergedRanges = mergeRanges(makeSignalRanges(downscaled));

        const normalizedSummaries = normalizeSummaries(
          downscaled,
          mergedRanges
        );

        setSummaries(loadedSummaries.removeAll(keysToRemove));
        setNormalizedDownscaledSummaries(normalizedSummaries);
      })
      .catch((err) => {
        enqueueSnackbar(
          _.get(err, ['message'], 'Failed to downscale signals.'),
          { variant: 'error' }
        );
      });
  }, [
    normalizedSignalPlots,
    needToDownscale,
    timelineDomain,
    summaries,
    enqueueSnackbar,
  ]);

  return (
    <ReactResizeDetector handleWidth handleHeight>
      {({ width, height }: { width: number; height: number }): JSX.Element => {
        return (
          <div style={{ height: '100%' }}>
            <VictoryChart
              theme={VictoryTheme.grayscale}
              domain={timelineDomain}
              padding={{ top: 0, left: 30, right: 30, bottom: 40 }}
              width={width}
              height={height}
              scale={{
                x: 'linear',
                y: 'linear',
              }}
              containerComponent={
                <VictoryBrushContainer
                  responsive={false}
                  brushDimension="x"
                  brushDomain={{
                    x: props.brushDomain,
                    y: [0, 1],
                  }}
                  defaultBrushArea="move"
                  onBrushDomainChange={handleBrushDomainChange}
                />
              }
            >
              <VictoryAxis
                tickValues={ticks.toArray()}
                tickFormat={(x): string => moment(x).format('MMM D')}
              />

              {normalizedSignalPlots
                .filter((sp) => sp.visible)
                .map((sp) => (
                  <VictoryArea
                    interpolation="monotoneX"
                    key={sp.signal.id}
                    data={
                      normalizedDownscaledSummaries.get(sp.signal?.id ?? '', {
                        points: [],
                      }).points
                    }
                    x={'t'}
                    y0={'min'}
                    y={'max'}
                    style={{
                      data: sp.styles?.area,
                    }}
                  />
                ))
                .valueSeq()}

              {normalizedSignalPlots
                .filter((sp) => sp.visible)
                .map((sp) => (
                  <VictoryLine
                    interpolation="monotoneX"
                    key={sp.signal.id}
                    data={
                      normalizedDownscaledSummaries.get(sp.signal?.id ?? '', {
                        points: [],
                      }).points
                    }
                    x={'t'}
                    y={'mean'}
                    style={{
                      data: sp.styles?.line,
                    }}
                  />
                ))
                .valueSeq()}
            </VictoryChart>
          </div>
        );
      }}
    </ReactResizeDetector>
  );
}
